import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import { Check, HelpCircle, X, User, ScanFace } from "@digital-twins/design-system/icons";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/Text";
import type { MaterialHelpProps } from "@/types/creative-library";

export function MaterialHelpDialog({ kind, className }: MaterialHelpProps) {
  const { t } = useTranslation("app");
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn("h-11 w-11", className)}
          aria-label={t("creativeLibrary.helpAction")}
          data-testid={`material-help-${kind}`}
        >
          <HelpCircle className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent
        className="max-h-[90dvh] max-w-lg gap-5 overflow-y-auto p-5 sm:p-6"
        data-testid={`material-help-dialog-${kind}`}
      >
        <DialogHeader className="pr-7 text-left">
          <DialogTitle>{t(`creativeLibrary.help.${kind}.title`)}</DialogTitle>
          <DialogDescription>
            {t(`creativeLibrary.help.${kind}.body`)}
          </DialogDescription>
        </DialogHeader>
        <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-3 rounded-xl border border-border p-4">
            <div className="flex h-36 items-center justify-center rounded-lg bg-background">
              {kind === "identity" ? (
                <div
                  className="flex items-center gap-3 text-primary"
                  aria-hidden="true"
                >
                  <User className="h-16 w-16" />
                  <ScanFace className="h-12 w-12" />
                </div>
              ) : kind === "logo" ? (
                <span className="border border-dashed border-border px-5 py-4 text-2xl font-semibold tracking-tight text-primary">
                  ZEST
                </span>
              ) : kind === "style" ? (
                <div
                  className="grid w-full grid-cols-2 gap-2 p-3"
                  aria-hidden="true"
                >
                  <div className="h-16 rounded-lg bg-primary/20" />
                  <div className="h-16 rounded-lg bg-primary/70" />
                  <div className="col-span-2 rounded-lg border border-border bg-background p-2 text-xs text-primary">
                    ZEST · Fresh by nature
                  </div>
                </div>
              ) : kind === "board" ? (
                <div className="flex w-full flex-col gap-3 px-3">
                  <div className="h-2 w-2/3 rounded bg-primary/40" />
                  <div className="flex items-end justify-center gap-2">
                    <div className="h-20 w-10 rounded-lg border-2 border-primary bg-background text-center text-[8px] leading-[4rem] text-primary">
                      ZEST
                    </div>
                    <div className="h-7 w-7 rounded-full bg-primary/30" />
                  </div>
                </div>
              ) : (
                <div className="flex h-28 w-16 flex-col items-center justify-center gap-2 rounded-xl border-2 border-primary bg-background text-primary">
                  <span className="text-xs font-medium">ZEST</span>
                  <span className="h-4 w-4 rounded-full bg-primary/20" />
                  <span className="text-xs">LYCHEE</span>
                </div>
              )}
            </div>
            <Text size="sm" className="inline-flex items-center gap-2">
              <Check className="h-4 w-4 text-primary" />
              {t("creativeLibrary.goodSource")}
            </Text>
            <Text size="xs" color="muted">
              {t("creativeLibrary.goodSourceHint")}
            </Text>
          </div>
          <div className="flex flex-col gap-3 rounded-xl border border-border p-4">
            <div className="flex h-36 items-center justify-center overflow-hidden rounded-lg bg-background">
              <div className="h-36 w-24 rotate-12 rounded-xl border-2 border-muted-foreground/30 bg-muted-foreground/10 blur-sm">
                <div className="mt-14 h-10 bg-muted-foreground/20" />
              </div>
            </div>
            <Text size="sm" className="inline-flex items-center gap-2">
              <X className="h-4 w-4 text-destructive" />
              {t("creativeLibrary.poorSource")}
            </Text>
            <Text size="xs" color="muted">
              {t("creativeLibrary.poorSourceHint")}
            </Text>
          </div>
        </div>
        <Text size="sm" color="muted">
          {t("creativeLibrary.formatHint")}
        </Text>
        <Text size="xs" color="muted">
          {t("creativeLibrary.fictionalExample")}
        </Text>
      </DialogContent>
    </Dialog>
  );
}
